// Product Request Form - displayed for NoCatalogue stage type
// Renders the brand's custom form fields for adding products to the order

import { useState, useCallback, type ChangeEvent } from "react";
import { Plus } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
  toast,
} from "@repo/ui";

interface FormField {
  enabled: boolean;
  key: string;
  label?: string | null;
  required: boolean;
}

export interface NoCatalogueConfig {
  additionalDetails?: string | null;
  formFields: FormField[];
}

interface ProductRequestFormProps {
  noCatalogueConfig: NoCatalogueConfig;
  onAddToCart: (formData: Record<string, string>) => void;
  /** How many more items can be added before hitting the max. null = no limit. */
  remainingQuantity?: number | null;
}

// Human-readable label for known form field keys
function getFieldLabel(field: FormField): string {
  if (field.label) return field.label;

  // Map common keys to readable labels
  const keyLabels: Record<string, string> = {
    productName: "Product Name",
    productSKU: "Product SKU",
    productURL: "Product URL",
    productPreferences: "Size, Color",
    quantity: "Qty",
    product_name: "Product Name",
    product_url: "Product URL",
    product_description: "Product Description",
    size: "Size",
    color: "Color",
    notes: "Notes",
    preferences: "Preferences",
    shipping_address: "Shipping Address",
  };

  return (
    keyLabels[field.key] ??
    field.key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

// Determine if a field should render as a textarea
function isTextareaField(key: string): boolean {
  const textareaKeys = [
    "product_description",
    "notes",
    "preferences",
    "shipping_address",
    "additional_details",
  ];
  return textareaKeys.includes(key);
}

// Determine the input type for a field
function getInputType(key: string): string {
  if (key === "quantity") return "number";
  if (key === "productURL" || key === "product_url") return "url";
  return "text";
}

export function ProductRequestForm({
  noCatalogueConfig,
  onAddToCart,
  remainingQuantity,
}: ProductRequestFormProps) {
  const enabledFields = noCatalogueConfig.formFields.filter((f) => f.enabled);

  const createInitialFormData = useCallback(() => {
    const initial: Record<string, string> = {};
    for (const field of enabledFields) {
      initial[field.key] = "";
    }
    return initial;
  }, [enabledFields]);

  const [formData, setFormData] = useState<Record<string, string>>(
    createInitialFormData,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = useCallback((key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return prev;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    for (const field of enabledFields) {
      const value = formData[field.key] ?? "";
      if (field.required && !value.trim()) {
        newErrors[field.key] = `${getFieldLabel(field)} is required`;
        continue;
      }
      if (field.key === "quantity" && value.trim()) {
        const qty = Number(value);
        if (!Number.isFinite(qty) || qty <= 0) {
          newErrors[field.key] = "Quantity must be at least 1";
        } else if (remainingQuantity != null && qty > remainingQuantity) {
          newErrors[field.key] =
            remainingQuantity <= 0
              ? "Maximum item limit reached"
              : `Only ${remainingQuantity} more ${remainingQuantity === 1 ? "item" : "items"} can be added`;
        }
      }
    }

    // If no quantity field exists, treat each add as qty 1 and check the limit
    const hasQuantityField = enabledFields.some((f) => f.key === "quantity");
    if (
      !hasQuantityField &&
      remainingQuantity != null &&
      remainingQuantity <= 0
    ) {
      newErrors._limit = "Maximum item limit reached";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [enabledFields, formData, remainingQuantity]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) {
        return;
      }
      onAddToCart(formData);
      setFormData(createInitialFormData());
      setErrors({});
      toast.success("Product added to order");
    },
    [validate, formData, onAddToCart, createInitialFormData],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add Product</CardTitle>
        <CardDescription>
          Fill in the details and add to your order
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {enabledFields.map((field) => {
            const label = getFieldLabel(field);
            const isTextarea = isTextareaField(field.key);
            const error = errors[field.key];

            return (
              <div key={field.key} className="space-y-2">
                <Label htmlFor={`field-${field.key}`}>
                  {label}
                  {field.required && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                {isTextarea ? (
                  <Textarea
                    id={`field-${field.key}`}
                    value={formData[field.key] ?? ""}
                    onChange={(
                      e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
                    ) => handleChange(field.key, e.target.value)}
                    placeholder={`Enter ${label}`}
                    rows={3}
                    className={error ? "border-destructive" : ""}
                  />
                ) : (
                  <Input
                    id={`field-${field.key}`}
                    type={getInputType(field.key)}
                    value={formData[field.key] ?? ""}
                    onChange={(
                      e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
                    ) => handleChange(field.key, e.target.value)}
                    placeholder={`Enter ${label}`}
                    min={field.key === "quantity" ? 1 : undefined}
                    max={
                      field.key === "quantity" && remainingQuantity != null
                        ? Math.max(remainingQuantity, 0)
                        : undefined
                    }
                    step={field.key === "quantity" ? 1 : undefined}
                    className={error ? "border-destructive" : ""}
                  />
                )}
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            );
          })}

          {errors._limit && (
            <p className="text-sm text-destructive">{errors._limit}</p>
          )}

          <Button
            type="submit"
            className="w-full mt-6"
            disabled={remainingQuantity != null && remainingQuantity <= 0}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add to Order
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
